import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Loader2, TrendingUp, TrendingDown, Minus, ArrowRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { useAuth } from "../../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n) || 0);
const pct = (n) => (n === null || n === undefined ? "—" : `${Number(n).toFixed(1)}%`);

/**
 * CostByBusinessLine
 * Vista comparativa de costes por línea de negocio Valoriza (Recogida, Surge,
 * Valoriza Eco, etc.) con:
 *  - Gráfico de barras planeado vs incurrido
 *  - Selector "A vs B" para comparar dos líneas por métrica
 *  - Detalle expandible con top 5 iniciativas
 */
export default function CostByBusinessLine() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [lineA, setLineA] = useState(null);
  const [lineB, setLineB] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/cost-comparison/by-business-line?year=${year}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok && alive) setData(await r.json());
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [year, token]);

  const rows = data?.rows || [];
  useEffect(() => {
    if (rows.length >= 2 && !lineA) {
      setLineA(rows[0].business_line);
      setLineB(rows[1].business_line);
    }
  }, [rows, lineA]);

  const chartData = useMemo(() =>
    rows.slice(0, 10).map((r) => ({
      name: (r.business_line || "").length > 14 ? `${r.business_line.slice(0, 14)}…` : r.business_line,
      full: r.business_line,
      Planeado: Math.round(r.total_planned),
      Incurrido: Math.round(r.total_incurred),
      Externo: Math.round(r.total_external),
    })), [rows]);

  const A = rows.find((r) => r.business_line === lineA);
  const B = rows.find((r) => r.business_line === lineB);

  if (loading) return <div className="p-8 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600"/></div>;
  if (!data || rows.length === 0) {
    return <div className="p-8 text-center text-sm text-muted-foreground">Sin datos de costes por línea todavía.</div>;
  }

  return (
    <div className="space-y-6" data-testid="cost-by-business-line">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Analítica · Costes</div>
          <h2 className="text-xl font-black tracking-tight">Comparativa por Línea de Negocio</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Reparto de coste planificado, incurrido y financiación externa entre las líneas Valoriza. Selecciona dos para ver la diferencia.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground">Año</label>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="text-xs px-2 py-1 rounded-md border border-border bg-background"
            data-testid="cost-line-year"
          >
            {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* Totals ribbon */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Planeado total" value={eur(data.totals.planned)} tone="emerald" />
        <StatCard label="Incurrido" value={eur(data.totals.incurred)} tone="blue" />
        <StatCard label="Financiación externa" value={eur(data.totals.external)} tone="violet" />
        <StatCard label={`${data.totals.lines} líneas · ${data.totals.project_count} proyectos`} value={pct(data.totals.planned ? 100 * data.totals.incurred / data.totals.planned : null)} tone="amber" sub="ejecución global"/>
      </div>

      {/* Chart */}
      <div className="rounded-2xl border border-border p-4 bg-card">
        <div className="text-xs font-bold text-muted-foreground mb-3 uppercase tracking-wide">Distribución de coste (top 10 líneas)</div>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 10, right: 20, bottom: 40, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2}/>
              <XAxis dataKey="name" angle={-25} textAnchor="end" height={60} tick={{ fontSize: 11 }}/>
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1_000_000 ? `${(v/1_000_000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v}/>
              <Tooltip formatter={(v) => eur(v)} labelFormatter={(l, p) => p?.[0]?.payload?.full || l}/>
              <Legend wrapperStyle={{ fontSize: 11 }}/>
              <Bar dataKey="Planeado" fill="#10b981" radius={[4, 4, 0, 0]}/>
              <Bar dataKey="Incurrido" fill="#3b82f6" radius={[4, 4, 0, 0]}/>
              <Bar dataKey="Externo" fill="#8b5cf6" radius={[4, 4, 0, 0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* A vs B comparator */}
      <div className="rounded-2xl border border-border p-4 bg-card space-y-3" data-testid="cost-line-compare">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Comparador A vs B</span>
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase text-emerald-600 font-bold">A</span>
            <select
              value={lineA || ""}
              onChange={(e) => setLineA(e.target.value)}
              className="text-xs px-2 py-1 rounded-md border border-border bg-background max-w-[220px]"
              data-testid="cost-line-select-a"
            >
              {rows.map((r) => <option key={`a-${r.business_line}`} value={r.business_line}>{r.business_line}</option>)}
            </select>
            <ArrowRight className="w-3 h-3 text-muted-foreground"/>
            <span className="text-[10px] uppercase text-blue-600 font-bold">B</span>
            <select
              value={lineB || ""}
              onChange={(e) => setLineB(e.target.value)}
              className="text-xs px-2 py-1 rounded-md border border-border bg-background max-w-[220px]"
              data-testid="cost-line-select-b"
            >
              {rows.map((r) => <option key={`b-${r.business_line}`} value={r.business_line}>{r.business_line}</option>)}
            </select>
          </div>
        </div>

        {A && B ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <LineCard row={A} accent="emerald"/>
            <LineCard row={B} accent="blue"/>
            <div className="md:col-span-2 border-t border-border pt-3 mt-1">
              <div className="text-[10px] uppercase font-bold text-muted-foreground mb-2">Diferencias A vs B</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <DiffCard label="Planeado" a={A.total_planned} b={B.total_planned} format={eur}/>
                <DiffCard label="Incurrido" a={A.total_incurred} b={B.total_incurred} format={eur}/>
                <DiffCard label="Ejecución" a={A.execution_pct} b={B.execution_pct} format={pct} isPct/>
                <DiffCard label="Coste medio init." a={A.avg_cost_per_initiative} b={B.avg_cost_per_initiative} format={eur}/>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-xs text-muted-foreground italic">Selecciona dos líneas para compararlas.</div>
        )}
      </div>

      {/* Full table */}
      <div className="rounded-2xl border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-muted/30">
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Todas las líneas</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase text-muted-foreground bg-muted/20">
              <tr>
                <th className="text-left px-3 py-2">Línea</th>
                <th className="text-right px-3 py-2">Proyectos</th>
                <th className="text-right px-3 py-2">Pilotos</th>
                <th className="text-right px-3 py-2">Planeado</th>
                <th className="text-right px-3 py-2">Incurrido</th>
                <th className="text-right px-3 py-2">Ejecución</th>
                <th className="text-right px-3 py-2">Externo</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.business_line} className={idx % 2 ? "bg-muted/10" : ""} data-testid={`cost-line-row-${r.business_line}`}>
                  <td className="px-3 py-2 font-semibold">{r.business_line}</td>
                  <td className="px-3 py-2 text-right">{Math.round(r.pg07_projects || 0)}</td>
                  <td className="px-3 py-2 text-right">{r.pilot_count || 0}</td>
                  <td className="px-3 py-2 text-right text-emerald-700 dark:text-emerald-400 font-mono">{eur(r.total_planned)}</td>
                  <td className="px-3 py-2 text-right text-blue-700 dark:text-blue-400 font-mono">{eur(r.total_incurred)}</td>
                  <td className="px-3 py-2 text-right">{pct(r.execution_pct)}</td>
                  <td className="px-3 py-2 text-right text-violet-700 dark:text-violet-400 font-mono">{eur(r.total_external)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, tone = "emerald" }) {
  const tones = {
    emerald: "text-emerald-700 border-emerald-200 bg-emerald-50/50 dark:bg-emerald-500/5 dark:text-emerald-300",
    blue: "text-blue-700 border-blue-200 bg-blue-50/50 dark:bg-blue-500/5 dark:text-blue-300",
    violet: "text-violet-700 border-violet-200 bg-violet-50/50 dark:bg-violet-500/5 dark:text-violet-300",
    amber: "text-amber-700 border-amber-200 bg-amber-50/50 dark:bg-amber-500/5 dark:text-amber-300",
  };
  return (
    <div className={`rounded-2xl border p-3 ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-80 font-bold">{label}</div>
      <div className="text-xl font-black leading-tight">{value}</div>
      {sub && <div className="text-[10px] mt-1 opacity-70">{sub}</div>}
    </div>
  );
}

function LineCard({ row, accent }) {
  const accents = {
    emerald: "from-emerald-500 to-emerald-700",
    blue: "from-blue-500 to-blue-700",
  };
  return (
    <motion.div layout className="rounded-2xl overflow-hidden border border-border">
      <div className={`bg-gradient-to-br ${accents[accent]} text-white px-4 py-3`}>
        <div className="text-[10px] uppercase tracking-widest opacity-80">Línea {accent === "emerald" ? "A" : "B"}</div>
        <div className="text-lg font-black leading-tight">{row.business_line}</div>
        <div className="text-[11px] opacity-80 mt-0.5">
          {Math.round(row.pg07_projects || 0)} proyectos PG-07 · {row.pilot_count || 0} pilotos
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 p-3">
        <MiniMetric label="Planeado" value={eur(row.total_planned)}/>
        <MiniMetric label="Incurrido" value={eur(row.total_incurred)}/>
        <MiniMetric label="Ejecución" value={pct(row.execution_pct)}/>
        <MiniMetric label="Coste medio init." value={eur(row.avg_cost_per_initiative)}/>
        <MiniMetric label="Financiación externa" value={eur(row.total_external)}/>
        <MiniMetric label="% Subvención" value={pct(row.subsidy_pct)}/>
      </div>
      {row.initiatives?.length > 0 && (
        <div className="border-t border-border px-3 py-2">
          <div className="text-[10px] uppercase text-muted-foreground font-bold mb-1">Top iniciativas</div>
          <ul className="space-y-1">
            {row.initiatives.slice(0, 4).map((it) => (
              <li key={it.id} className="text-xs flex items-center justify-between gap-2">
                <span className="truncate flex-1">{it.name}</span>
                <span className="text-[10px] font-mono text-muted-foreground">{eur(it.planned)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </motion.div>
  );
}

function MiniMetric({ label, value }) {
  return (
    <div className="rounded-lg bg-muted/40 px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-bold">{value}</div>
    </div>
  );
}

function DiffCard({ label, a, b, format, isPct = false }) {
  const va = Number(a || 0);
  const vb = Number(b || 0);
  const diff = va - vb;
  const noData = (a === null || a === undefined) && (b === null || b === undefined);
  const isPositive = diff > 0;
  const pctChange = vb !== 0 ? (diff / vb) * 100 : null;
  const arrow = noData ? Minus : isPositive ? TrendingUp : diff < 0 ? TrendingDown : Minus;
  const Icon = arrow;
  const tone = noData || diff === 0 ? "text-muted-foreground" : isPositive ? "text-emerald-600" : "text-red-500";

  return (
    <div className="rounded-lg border border-border p-3 bg-card">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="flex items-baseline gap-2 mt-1">
        <span className="text-base font-black">{noData ? "—" : format(diff)}</span>
        {!noData && (
          <span className={`text-[11px] font-bold ${tone} flex items-center gap-0.5`}>
            <Icon className="w-3 h-3"/>
            {isPct ? "" : (pctChange !== null ? `${pctChange > 0 ? "+" : ""}${pctChange.toFixed(1)}%` : "")}
          </span>
        )}
      </div>
      <div className="text-[10px] text-muted-foreground mt-1">
        A {format(a || 0)} · B {format(b || 0)}
      </div>
    </div>
  );
}
